import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, Navigate, useSearchParams } from "react-router-dom";
import {
  languages,
  isRtlLanguage,
  useScene,
  useScenes,
  type SceneItem,
} from "@/lib/scenes";
import AppHeader from "@/components/AppHeader";

function chooseVoiceForLanguage(locale: string): SpeechSynthesisVoice | null {
  const normalized = locale.toLowerCase();
  const base = normalized.slice(0, 2);
  const voices = speechSynthesis.getVoices().filter((v) => {
    const vl = v.lang.toLowerCase();
    return vl === normalized || vl.startsWith(`${normalized}-`) || vl.startsWith(base);
  });
  return (
    voices.find((v) => v.lang.toLowerCase() === normalized) ??
    voices.find((v) => v.lang.toLowerCase().startsWith(`${normalized}-`)) ??
    voices.find((v) => v.default) ??
    voices[0] ??
    null
  );
}

export default function LessonPage() {
  const [params, setParams] = useSearchParams();
  const sceneId = params.get("scene") ?? "";
  const initialLang = params.get("lang") ?? "es";
  const [currentLanguage, setCurrentLanguage] = useState<string>(
    languages[initialLang] ? initialLang : "es"
  );
  // Distinguish "still loading scenes list" from "scene not found": if the list
  // has arrived and the slug isn't in it, redirect to /scenes. Otherwise render
  // a blank shell until the fetch resolves.
  const allScenes = useScenes();
  const scene = useScene(sceneId);
  const sceneWidth = scene?.width ?? 1536;
  const sceneHeight = scene?.height ?? 1024;

  const [dbItems, setDbItems] = useState<SceneItem[] | null>(null);
  const [currentItem, setCurrentItem] = useState<SceneItem | null>(null);
  const [seenWords, setSeenWords] = useState<Set<string>>(() => new Set());

  useEffect(() => {
    if (!sceneId) return;
    let cancelled = false;
    fetch(`/api/scenes/${sceneId}/items`, { credentials: "same-origin" })
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        if (cancelled) return;
        if (data?.items?.length > 0) {
          const fetched = (data.items as SceneItem[]).map((it) => {
            const pts = it.shape?.points;
            return { ...it, shape: { ...it.shape, points: Array.isArray(pts) ? pts : [] } };
          });
          setDbItems(fetched);
          setCurrentItem((prev) => prev ?? fetched[0]);
          setSeenWords((prev) => prev.size === 0 ? new Set([fetched[0].english]) : prev);
        }
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [sceneId]);

  const sceneItems = useMemo<SceneItem[]>(() => dbItems ?? [], [dbItems]);
  const currentAudioRef = useRef<HTMLAudioElement | null>(null);

  const direction = isRtlLanguage(currentLanguage) ? "rtl" : "ltr";
  const localeCode = languages[currentLanguage]?.locale ?? "en";

  // Apply document direction for target-language areas
  useEffect(() => {
    document.documentElement.lang = localeCode;
  }, [localeCode]);

  const stopAudio = useCallback(() => {
    speechSynthesis.cancel();
    if (currentAudioRef.current) {
      currentAudioRef.current.pause();
      currentAudioRef.current.currentTime = 0;
      currentAudioRef.current = null;
    }
  }, []);

  const speak = useCallback(
    (item: SceneItem | null, language: string) => {
      if (!item) return;
      const lang = languages[language];
      if (!lang) return;
      const translation = item.translations[language];
      if (!translation) return;
      const voice = chooseVoiceForLanguage(lang.locale);
      const useServerTts = language === "ar" && !voice;
      stopAudio();
      if (useServerTts) {
        const audio = new Audio(
          `/api/tts?text=${encodeURIComponent(translation.text)}&lang=${encodeURIComponent(language)}`
        );
        currentAudioRef.current = audio;
        audio.play().catch(() => {
          // swallow — phonetic still visible
        });
        return;
      }
      const utterance = new SpeechSynthesisUtterance(translation.text);
      utterance.lang = lang.locale;
      utterance.rate = language === "ja" ? 0.9 : language === "ar" ? 0.92 : 0.95;
      if (voice) {
        utterance.voice = voice;
        utterance.lang = voice.lang;
      }
      speechSynthesis.resume();
      speechSynthesis.speak(utterance);
    },
    [stopAudio]
  );

  const handleSelect = useCallback(
    (itemId: string, shouldSpeak = false) => {
      const item = sceneItems.find((i) => i.id === itemId) ?? null;
      if (!item) return;
      setCurrentItem(item);
      setSeenWords((prev) => {
        if (prev.has(item.english)) return prev;
        const next = new Set(prev);
        next.add(item.english);
        return next;
      });
      if (shouldSpeak) speak(item, currentLanguage);
    },
    [sceneItems, speak, currentLanguage]
  );

  const handleLanguageChange = useCallback(
    (newLang: string) => {
      setCurrentLanguage(newLang);
      const p = new URLSearchParams(params);
      p.set("lang", newLang);
      setParams(p, { replace: true });
      if (currentItem) {
        setTimeout(() => speak(currentItem, newLang), 0);
      }
    },
    [params, setParams, currentItem, speak]
  );

  const replayCurrent = useCallback(() => {
    if (currentItem) speak(currentItem, currentLanguage);
  }, [currentItem, currentLanguage, speak]);

  // Deduped vocab by english word
  const vocabWords = useMemo(() => {
    const seen = new Set<string>();
    const out: SceneItem[] = [];
    for (const item of sceneItems) {
      if (seen.has(item.english)) continue;
      seen.add(item.english);
      out.push(item);
    }
    return out;
  }, [sceneItems]);

  const currentTranslation = currentItem?.translations[currentLanguage];

  // Redirect only once the scenes list has loaded and confirms the slug is missing.
  if (allScenes !== null && !scene) {
    return <Navigate to="/scenes" replace />;
  }
  if (!scene) return <div className="page-shell"><AppHeader /></div>;

  return (
    <div className="page-shell">
      <AppHeader />
      <header className="lesson-header">
        <div>
          <Link className="lesson-header-back" to={`/scenes?lang=${currentLanguage}`}>
            ← Back to scenes
          </Link>
          <h1>{scene.title}</h1>
        </div>
        <div className="lesson-controls">
          <label className="language-select-label">
            <span>Target language</span>
            <select
              value={currentLanguage}
              onChange={(e) => handleLanguageChange(e.target.value)}
            >
              {Object.entries(languages).map(([code, lang]) => (
                <option key={code} value={code}>
                  {lang.label}
                </option>
              ))}
            </select>
          </label>
        </div>
      </header>

      <main className="lesson-layout">
        <section className="scene" style={{ aspectRatio: `${sceneWidth} / ${sceneHeight}` }}>
          <img
            className="scene-art"
            src={scene.image}
            alt={scene.alt}
            width={sceneWidth}
            height={sceneHeight}
            decoding="async"
          />
          <svg
            className="hotspots"
            viewBox={`0 0 ${sceneWidth} ${sceneHeight}`}
            xmlns="http://www.w3.org/2000/svg"
          >
            {sceneItems.map((item) => {
              const isActive =
                currentItem !== null && currentItem.english === item.english;
              // Support both single polygon (shape.points) and multi-polygon (shape.points is array of arrays)
              const polygons: [number, number][][] =
                item.shape.points.length > 0 && Array.isArray(item.shape.points[0]) && typeof item.shape.points[0][0] === "number"
                  ? [item.shape.points as [number, number][]]
                  : (item.shape.points as unknown as [number, number][][]);
              return polygons.map((poly, pi) => (
                <polygon
                  key={`${item.id}-${pi}`}
                  className={`hotspot-region${isActive ? " is-active" : ""}`}
                  data-item-id={item.id}
                  tabIndex={pi === 0 ? 0 : -1}
                  role="button"
                  aria-label={`Learn ${item.english}`}
                  points={poly.map((p) => p.join(",")).join(" ")}
                  onClick={() => handleSelect(item.id, true)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      handleSelect(item.id, true);
                    }
                  }}
                />
              ));
            })}
          </svg>
        </section>

        <aside className="lesson-side">
          <div className="focus-card">
            <p className="section-label">Current word</p>
            <h2 id="target-word" lang={localeCode} dir={direction}>
              {currentTranslation?.text ?? "Choose an object"}
            </h2>
            <p id="phonetic">{currentTranslation?.phonetic ?? "Pronunciation guide appears here."}</p>
            <p id="native-word" lang="en" dir="ltr">
              {currentItem?.english ?? "English meaning will appear here."}
            </p>
            <div className="focus-actions">
              <button
                type="button"
                className="primary-button"
                onClick={replayCurrent}
                disabled={!currentItem}
              >
                🔊 Play again
              </button>
            </div>
          </div>

          <div className="vocab-card">
            <div className="vocab-header">
              <p className="section-label">Vocabulary</p>
              <p id="progress-text">
                {seenWords.size} / {vocabWords.length} explored
              </p>
            </div>
            <ul id="vocab-list" className="vocab-list" dir={direction}>
              {vocabWords.map((item) => {
                const translation = item.translations[currentLanguage];
                return (
                  <li
                    key={item.id}
                    className={`vocab-item${seenWords.has(item.english) ? " is-seen" : ""}`}
                  >
                    <strong lang={localeCode} dir={direction}>
                      {translation?.text ?? item.english}
                    </strong>
                    <span>{item.english}</span>
                  </li>
                );
              })}
            </ul>
          </div>
        </aside>
      </main>
    </div>
  );
}
